import React, { useRef } from 'react';
import { Box, keyframes } from '@mui/material';
import { CQ_LOGO_ASCII, CQ_TAGLINE } from './asciiLogo';
import { APP_VERSION } from '../utils/version';
import { designTokens } from '../theme';

// Shimmer animation - gradient slides across text (like Claude thinking)
const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// Subtle pulse on tagline
const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

// Progress bar animation
const progressFill = keyframes`
  0% {
    width: 0%;
  }
  50% {
    width: 65%;
  }
  80% {
    width: 85%;
  }
  100% {
    width: 100%;
  }
`;

export const BrandedLoading: React.FC<{ debugSource?: string }> = ({ debugSource }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isRouteGate = debugSource === 'project-search-gate';

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: designTokens.colors.bg.primary,
        ...(isRouteGate
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
            }
          : {
              height: '100%',
              width: '100%',
            }),
      }}
    >
      {/* ASCII Logo with shimmer effect */}
      <Box
        component="pre"
        sx={{
          fontFamily: designTokens.typography.fontMono,
          fontSize: 20,
          lineHeight: 1.15,
          letterSpacing: '0.02em',
          color: designTokens.colors.text.muted,
          background: `linear-gradient(
            90deg,
            ${designTokens.colors.text.muted} 25%,
            ${designTokens.colors.text.primary} 50%,
            ${designTokens.colors.text.muted} 75%
          )`,
          backgroundSize: '200% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: `${shimmer} 2.5s ease-in-out infinite`,
          userSelect: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {CQ_LOGO_ASCII}
      </Box>

      {/* Version */}
      <Box
        sx={{
          fontFamily: designTokens.typography.fontMono,
          fontSize: 16,
          color: designTokens.colors.text.secondary,
          mt: 3,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
        }}
      >
        Creative Queue v{APP_VERSION}
      </Box>

      {/* Tagline with pulse */}
      <Box
        sx={{
          fontFamily: designTokens.typography.fontMono,
          fontSize: 14,
          color: designTokens.colors.text.muted,
          mt: 0.75,
          fontStyle: 'italic',
          animation: `${pulse} 2s ease-in-out infinite`,
        }}
      >
        {CQ_TAGLINE}
      </Box>

      {/* Progress bar container */}
      <Box
        sx={{
          mt: 5,
          width: 320,
          height: 4,
          bgcolor: designTokens.colors.bg.card,
          borderRadius: designTokens.spacing.radiusSm,
          overflow: 'hidden',
        }}
      >
        {/* Animated progress fill */}
        <Box
          sx={{
            height: '100%',
            bgcolor: designTokens.colors.text.muted,
            borderRadius: designTokens.spacing.radiusSm,
            animation: `${progressFill} 3s ease-out forwards`,
          }}
        />
      </Box>
    </Box>
  );
};
